import { useId, type ReactNode } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Chart,
  DenseTable,
  StatusBadge,
  useDialogFocus,
  type Column,
  type ChartData,
  type ChartSeries,
} from "../../components/index.ts";
import { ChartCard } from "../../components/observability/index.ts";
import { formatDateTime, isoAttr } from "../../lib/datetime.ts";
import type { ApiError } from "../../lib/api.ts";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  type McpToolDetail,
  type McpToolAgentUse,
  type McpToolDecisionRef,
} from "./use-mcp-gateway.ts";
import {
  formatCount,
  formatMs,
  formatRate,
  quantityMeasure,
  quantityValue,
  outcomeLabel,
} from "./vocab.ts";

/**
 * The per-tool detail drawer (task 7.2; requirement 13.2). It slides in from the right when a tool
 * row is clicked and shows that one tool's detail over `GET /api/v1/mcp/tools/{tool}`:
 *
 * - a latency histogram (p50/p95/p99 as a small bar chart over the shared Chart wrapper, so it keeps
 *   the kit's accessible data-table alternative),
 * - outcomes over time (one line per `tool_outcome` member through the ChartCard),
 * - the agents that used the tool (first use + calls), and
 * - the recent decisions behind the tool, each linked to its trace (`/traces/:id`) and event
 *   (`/events/:id`) so the operator can pivot from a tool to the exact decision and its waterfall.
 *
 * It is an accessible modal dialog: `useDialogFocus` moves focus in, traps Tab, closes on Escape and
 * restores focus to the trigger; the backdrop click closes it too, and it is portaled to the body so
 * the fixed backdrop is relative to the viewport. Rendered only when a tool is selected, so there is
 * no inert dialog in the tree when the drawer is closed.
 */
export function ToolDrawer(props: {
  readonly tool: string | null;
  readonly onClose: () => void;
  readonly query: UseQueryResult<McpToolDetail, ApiError>;
}): ReactNode {
  const panelRef = useDialogFocus<HTMLDivElement>(props.onClose);
  const titleId = useId();

  if (props.tool === null) return null;

  const { data, isError, error, isPending } = props.query;

  return createPortal(
    <div
      className="mcp-drawer-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) props.onClose();
      }}
    >
      <div
        ref={panelRef as React.Ref<HTMLDivElement>}
        className="mcp-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="mcp-drawer__header">
          <h2 className="mcp-drawer__title" id={titleId}>
            Tool · <code>{props.tool}</code>
          </h2>
          <button
            type="button"
            className="mcp-drawer__close"
            aria-label="Close tool detail"
            onClick={props.onClose}
          >
            ✕
          </button>
        </header>

        <div className="mcp-drawer__body">
          {isPending ? (
            <p className="mcp-drawer__state" role="status" aria-live="polite">
              Loading the detail for <code>{props.tool}</code>…
            </p>
          ) : isError ? (
            <p className="mcp-drawer__state" data-kind="error">
              {drawerErrorMessage(error)}
            </p>
          ) : data !== undefined ? (
            <ToolDetailBody detail={data} />
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function drawerErrorMessage(error: ApiError | null): string {
  if (error === null) return "The tool detail could not load.";
  switch (error.kind) {
    case "not_found":
      return "No detail was found for this tool in the current window.";
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see this tool. The server enforces access.";
    case "network":
      return "The tool-detail request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

function ToolDetailBody(props: { detail: McpToolDetail }): ReactNode {
  const { detail } = props;
  const s = detail.summary;

  return (
    <div className="mcp-drawer__sections">
      {/* Headline summary of the one tool. */}
      <dl className="mcp-kv">
        <div className="mcp-kv__row">
          <dt>Calls</dt>
          <dd data-measure={quantityMeasure(s.calls)}>{formatCount(s.calls)}</dd>
        </div>
        <div className="mcp-kv__row">
          <dt>Error rate</dt>
          <dd data-measure={quantityMeasure(s.error_rate)}>{formatRate(s.error_rate)}</dd>
        </div>
      </dl>

      <LatencyHistogram detail={detail} />
      <OutcomesOverTime detail={detail} />
      <AgentsSection agents={detail.agents} />
      <RecentDecisions decisions={detail.recent_decisions} />
    </div>
  );
}

/**
 * The latency histogram: p50/p95/p99 drawn as three bars over the shared Chart wrapper. A tiny
 * three-bucket histogram is the honest shape the contract gives us (percentiles, not a raw
 * distribution), and the Chart wrapper carries the always-present data-table alternative so the
 * exact figures are legible without the plot.
 */
function LatencyHistogram(props: { detail: McpToolDetail }): ReactNode {
  const s = props.detail.summary;
  const p50 = quantityValue(s.latency_p50_ms);
  const p95 = quantityValue(s.latency_p95_ms);
  const p99 = quantityValue(s.latency_p99_ms);

  const hasAny = p50 !== null || p95 !== null || p99 !== null;
  // uPlot needs aligned [xs, ys]; the bucket index is the x, the percentile ms is the y.
  const data: ChartData = [
    [0, 1, 2],
    [p50 ?? 0, p95 ?? 0, p99 ?? 0],
  ];
  const series: readonly ChartSeries[] = [
    { label: "Latency (ms)", colorVar: "--accent", measure: quantityMeasure(s.latency_p95_ms) },
  ];

  return (
    <section className="mcp-drawer__section" aria-label="Latency histogram">
      <h3 className="mcp-drawer__section-title">Latency</h3>
      {hasAny ? (
        <>
          <Chart
            title="Latency percentiles (p50 / p95 / p99)"
            data={data}
            series={series}
            x="linear"
            height={160}
            formatX={(v) => ["p50", "p95", "p99"][v] ?? String(v)}
            formatY={(v) => `${v.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`}
          />
          <span className="mcp-drawer__note">
            p50 {formatMs(s.latency_p50_ms)} · p95 {formatMs(s.latency_p95_ms)} · p99{" "}
            {formatMs(s.latency_p99_ms)}
          </span>
        </>
      ) : (
        <p className="mcp-drawer__state" data-kind="empty">
          No latency was recorded for this tool in the window.
        </p>
      )}
    </section>
  );
}

/**
 * Outcomes over time: one line per `tool_outcome` member, built from the per-step `by_outcome`
 * counts. The outcome set is discovered from the data (every step zero-fills the catalog members),
 * and the lines are plotted through the ChartCard so the operator sees the mix move over the window.
 */
function OutcomesOverTime(props: { detail: McpToolDetail }): ReactNode {
  const points = props.detail.outcomes_over_time;

  // Discover the stable outcome set across the series (the server zero-fills the catalog members).
  const outcomes: string[] = [];
  for (const p of points) {
    for (const o of p.by_outcome) {
      if (!outcomes.includes(o.outcome)) outcomes.push(o.outcome);
    }
  }

  const hasData = points.length > 0 && outcomes.length > 0;

  const xs = points.map((p) => p.t);
  const ys = outcomes.map((outcome) =>
    points.map((p) => {
      const match = p.by_outcome.find((o) => o.outcome === outcome);
      return match === undefined ? 0 : (quantityValue(match.count) ?? 0);
    }),
  );
  const data: ChartData = [xs, ...ys];
  const OUTCOME_COLORS: Readonly<Record<string, string>> = {
    ok: "--status-healthy",
    tool_error: "--status-error",
    upstream_error: "--status-error",
    timeout: "--status-warn",
    cancelled: "--status-unknown",
    other: "--status-unknown",
  };
  const series: readonly ChartSeries[] = outcomes.map((outcome) => ({
    label: outcomeLabel(outcome),
    colorVar: OUTCOME_COLORS[outcome] ?? "--accent",
  }));

  return (
    <section className="mcp-drawer__section" aria-label="Outcomes over time">
      <h3 className="mcp-drawer__section-title">Outcomes over time</h3>
      {hasData ? (
        <ChartCard
          title="Outcomes over time"
          data={data}
          series={series}
          height={200}
          empty="No outcomes were recorded for this tool in the window."
        />
      ) : (
        <p className="mcp-drawer__state" data-kind="empty">
          No outcomes were recorded for this tool in the window.
        </p>
      )}
    </section>
  );
}

/** The agents that used the tool, with their call count and first use. */
function AgentsSection(props: { agents: readonly McpToolAgentUse[] }): ReactNode {
  const columns: readonly Column<McpToolAgentUse>[] = [
    { key: "agent", header: "Agent", isRowHeader: true, cell: (r) => <code>{r.agent_sub}</code> },
    {
      key: "calls",
      header: "Calls",
      align: "right",
      cell: (r) => formatCount(r.calls),
    },
    {
      key: "first",
      header: "First use",
      align: "right",
      cell: (r) =>
        r.first_use_at === null ? (
          "—"
        ) : (
          <time dateTime={isoAttr(r.first_use_at)}>{formatDateTime(r.first_use_at)}</time>
        ),
    },
  ];

  return (
    <section className="mcp-drawer__section" aria-label="Agents">
      <h3 className="mcp-drawer__section-title">Agents</h3>
      <DenseTable
        caption="Agents that called this tool"
        captionHidden
        columns={columns}
        rows={props.agents}
        rowKey={(r) => r.agent_sub}
        emptyMessage="No agent called this tool in the window."
      />
    </section>
  );
}

/**
 * The recent decisions behind the tool, newest first, each linked to its trace (`/traces/:id`) and
 * event (`/events/:id`). A decision that carries no trace/event simply shows a dash rather than a
 * dead link, so the pivot is honest about what it can reach.
 */
function RecentDecisions(props: { decisions: readonly McpToolDecisionRef[] }): ReactNode {
  const navigate = useNavigate();

  const columns: readonly Column<McpToolDecisionRef>[] = [
    {
      key: "ts",
      header: "When",
      cell: (r) => <time dateTime={isoAttr(r.ts)}>{formatDateTime(r.ts)}</time>,
    },
    {
      key: "decision",
      header: "Decision",
      cell: (r) =>
        r.decision === null ? (
          <StatusBadge level="unknown" label="n/a" size="sm" />
        ) : (
          <StatusBadge
            level={r.decision === "allow" ? "healthy" : "error"}
            label={r.decision === "allow" ? "Allow" : "Deny"}
            size="sm"
          />
        ),
    },
    {
      key: "outcome",
      header: "Outcome",
      cell: (r) => (r.outcome === null ? "—" : outcomeLabel(r.outcome)),
    },
    {
      key: "agent",
      header: "Agent",
      cell: (r) => (r.agent_sub === null ? "—" : <code>{r.agent_sub}</code>),
    },
    {
      key: "links",
      header: "Open",
      cell: (r) => (
        <span className="mcp-drawer__links">
          {r.trace_id !== null ? (
            <button
              type="button"
              className="mcp-link"
              onClick={() => navigate(`/traces/${encodeURIComponent(r.trace_id as string)}`)}
              aria-label={`Open the trace ${r.trace_id} for this decision`}
              title={r.trace_id}
            >
              Trace
            </button>
          ) : (
            <span className="mcp-drawer__nolink">no trace</span>
          )}
          {r.event_id !== null ? (
            <button
              type="button"
              className="mcp-link"
              onClick={() => navigate(`/events/${encodeURIComponent(r.event_id as string)}`)}
              aria-label={`Open the event ${r.event_id} for this decision`}
              title={r.event_id}
            >
              Event
            </button>
          ) : (
            <span className="mcp-drawer__nolink">no event</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <section className="mcp-drawer__section" aria-label="Recent decisions">
      <h3 className="mcp-drawer__section-title">Recent decisions</h3>
      <DenseTable
        caption="Recent decisions behind this tool, newest first"
        captionHidden
        columns={columns}
        rows={props.decisions}
        rowKey={(r) => r.decision_id}
        emptyMessage="No decisions behind this tool in the window."
      />
    </section>
  );
}
